import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import {
  normalizeSalesDisplayDuration,
  type SalesDisplaySlide,
  type SalesDisplaySlideKind,
  type SalesDisplayWidgetKey,
} from "@/lib/sales-display-slides";

const BUCKET = "sales-display-media";

interface RawSlide {
  id: string;
  kind: SalesDisplaySlideKind;
  title: string;
  body: string | null;
  media_path: string | null;
  widget_key: SalesDisplayWidgetKey | null;
  duration_seconds: number;
  sort_order: number;
  is_active: boolean;
}

async function signedUrl(path: string | null): Promise<string | null> {
  if (!path) return null;
  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(path, 60 * 60);
  if (error) throw error;
  return data.signedUrl;
}

function mapSlide(row: RawSlide, mediaUrl: string | null): SalesDisplaySlide {
  return {
    id: row.id,
    kind: row.kind,
    title: row.title,
    body: row.body,
    mediaPath: row.media_path,
    mediaUrl,
    widgetKey: row.widget_key,
    durationSeconds: normalizeSalesDisplayDuration(row.duration_seconds),
    sortOrder: row.sort_order,
    isActive: row.is_active,
  };
}

export function useSalesDisplaySlides(enabled = true) {
  return useQuery({
    queryKey: ["sales-display-slides"],
    enabled,
    staleTime: 60_000,
    refetchInterval: 10 * 60_000,
    queryFn: async (): Promise<SalesDisplaySlide[]> => {
      const { data, error } = await supabase
        .from("sales_display_slides")
        .select("id, kind, title, body, media_path, widget_key, duration_seconds, sort_order, is_active")
        .order("sort_order")
        .order("created_at");
      if (error) throw error;
      const rows = (data ?? []) as RawSlide[];
      const urls = await Promise.all(rows.map((row) => signedUrl(row.media_path)));
      return rows.map((row, index) => mapSlide(row, urls[index]));
    },
  });
}

export interface SaveSalesDisplaySlideInput {
  id?: string;
  kind: SalesDisplaySlideKind;
  title: string;
  body?: string | null;
  mediaPath?: string | null;
  widgetKey?: SalesDisplayWidgetKey | null;
  durationSeconds: number;
  sortOrder: number;
  isActive: boolean;
  createdBy?: string | null;
}

export function useSaveSalesDisplaySlide() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: SaveSalesDisplaySlideInput) => {
      const payload = {
        kind: input.kind,
        title: input.title.trim(),
        body: input.body?.trim() || null,
        media_path: input.mediaPath || null,
        widget_key: input.kind === "widget" ? input.widgetKey ?? null : null,
        duration_seconds: normalizeSalesDisplayDuration(input.durationSeconds),
        sort_order: input.sortOrder,
        is_active: input.isActive,
        ...(input.id ? {} : { created_by: input.createdBy ?? null }),
      };
      const query = input.id
        ? supabase.from("sales_display_slides").update(payload).eq("id", input.id)
        : supabase.from("sales_display_slides").insert(payload);
      const { error } = await query;
      if (error) throw error;
    },
    onSuccess: () => invalidateFor(queryClient, "sales_display_slides"),
  });
}

export function useDeleteSalesDisplaySlide() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (slide: SalesDisplaySlide) => {
      const { error } = await supabase.from("sales_display_slides").delete().eq("id", slide.id);
      if (error) throw error;
      if (slide.mediaPath) await supabase.storage.from(BUCKET).remove([slide.mediaPath]);
    },
    onSuccess: () => invalidateFor(queryClient, "sales_display_slides"),
  });
}

export function useReorderSalesDisplaySlides() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (slides: SalesDisplaySlide[]) => {
      await Promise.all(slides.map(async (slide, index) => {
        const { error } = await supabase
          .from("sales_display_slides")
          .update({ sort_order: (index + 1) * 10 })
          .eq("id", slide.id);
        if (error) throw error;
      }));
    },
    onSuccess: () => invalidateFor(queryClient, "sales_display_slides"),
  });
}

export async function uploadSalesDisplayMedia(file: File): Promise<string> {
  const extension = file.name.split(".").pop()?.toLowerCase().replace(/[^a-z0-9]/g, "") || "bin";
  const path = `${crypto.randomUUID()}.${extension}`;
  const { error } = await supabase.storage.from(BUCKET).upload(path, file, {
    contentType: file.type,
    upsert: false,
  });
  if (error) throw error;
  return path;
}
